
import { useState, type FC } from "react"
import { Link, useLocation, useNavigate } from "react-router"
import { motion } from "motion/react"
import {
  HomeLine,
  FileCheck02,
  ZapFast,
  Menu01,
  Settings01,
  Car01,
  BarChart10,
  BankNote03,
  LogOut01,
  // HIDE_SCHED_INC: CalendarDate (Scheduling) — re-add to the import above
  // when restoring this entry.
  ArrowRight,
  ChevronRight,
  Tool01,
  LayersThree01,
  User01,
  Inbox01,
  CalendarDate,
  Zap,
  Gift01,
  Trophy01,
  Wallet01,
  CheckDone01,
} from "@untitledui/icons"
import { useAuth } from "@/lib/auth/auth-context"
import { useOrganization } from "@/lib/organization/organization-context"
import { Sheet } from "@/components/layout/sheet"
import { ORG_VISUAL_TOKENS } from "@/lib/team-types/visual-tokens"
import { Roles, type Role } from "@/lib/team/roles"
import { useCurrentRole } from "@/lib/team/use-current-role"
import { cx } from "@/lib/utils/cx"

// Mobile bottom nav. The mental model:
//   1. The user's primary mobile job is to start a new estimate. The speed
//      dial occupies the dead-centre of the bar so it's one tap from
//      anywhere.
//   2. Home is the only visible companion — conventional landing-tab.
//   3. Everything else (Estimates, Schedule, Reports, Total Loss, Settings,
//      sign-out, team switcher) lives behind the More sheet —
//      present, but not eating mobile real estate.
//
// 3 slots total: [ Home ] · ⚡ Quick · [ More ]. The dial is centred via a
// 3-col grid (column 2 spans 33.3–66.7%, centre at 50%).

interface PrimaryTab {
  name: string
  href: string
  icon: FC<{ className?: string; strokeWidth?: number }>
  excludeChildren?: string
}

// 3-slot bottom nav: [ Home ] · ⚡ Quick · [ More ]. Estimates moves into
// the More sheet as the first item so it's still 2 taps away — that's the
// trade for a true-centred speed dial and a cleaner bar.
const PRIMARY_TABS: PrimaryTab[] = [
  { name: "Home", href: "/dashboard", icon: HomeLine },
]

interface MoreItem {
  name: string
  href: string
  icon: FC<{ className?: string; strokeWidth?: number }>
  description: string
  // Title-Case Role enum from @/lib/team/roles — must match the wire format
  // (TeamUser.role). `null` means "visible to everyone with a team".
  // Super-admins (`user.admin`) bypass the filter entirely below.
  roles: readonly Role[] | null
  /** Restrict to a team context (e.g. Payouts is a body-shop function).
   *  Unlike `roles`, this is contextual — admins don't bypass it. */
  teamKinds?: readonly ("dealership" | "bodyshop")[]
}

const MORE_ITEMS: MoreItem[] = [
  // Body shops first: the phone screen. Who to call, what to say, what
  // happens next — the job this device is for.
  {
    name: "Tasks",
    href: "/tasks",
    icon: CheckDone01,
    description: "Who to call and what happens next",
    roles: null,
    teamKinds: ["bodyshop"],
  },
  {
    name: "Estimates",
    href: "/estimates",
    icon: FileCheck02,
    description: "View, sort, and act on estimates",
    roles: null,
  },
  {
    name: "Inbox",
    href: "/inbox",
    icon: Inbox01,
    description: "Reply to customers & get them booked",
    roles: null,
  },
  {
    name: "Calendar",
    href: "/calendar",
    icon: CalendarDate,
    description: "Bookings & free space",
    roles: null,
  },
  {
    name: "Customers",
    href: "/customers",
    // User01 (a person) matches the sidebar — Users01 stays the Team icon (#59).
    icon: User01,
    description: "Look up a customer & their history",
    roles: null,
  },
  {
    name: "Marketing",
    href: "/marketing",
    icon: Zap,
    description: "What the follow-up is winning",
    roles: null,
  },
  {
    name: "Referrals",
    href: "/referrals",
    icon: Gift01,
    description: "Send a friend, get paid at completion",
    roles: null,
  },
  // HIDE_SCHED_INC: Scheduling hidden — features not ready. See HideSchedulingIncentives.md.
  // {
  //   name: "Schedule",
  //   href: "/scheduling",
  //   icon: CalendarDate,
  //   description: "Appointments and bookings",
  //   roles: [Roles.Estimator, Roles.ServiceAdvisor, Roles.ServiceManager, Roles.GeneralManager, Roles.Director],
  // },
  {
    name: "Reports",
    href: "/reports",
    icon: BarChart10,
    description: "Performance & trends",
    roles: [Roles.Director, Roles.GeneralManager, Roles.ServiceManager],
  },
  // Payouts are settled by the collision center funding the rewards
  // program — body-shop manager only, never the dealership side.
  {
    name: "Payouts",
    href: "/payouts",
    icon: BankNote03,
    description: "Approve & settle advisor pay",
    roles: [Roles.GeneralManager],
    teamKinds: ["bodyshop"],
  },
  {
    name: "Total Loss",
    href: "/total-loss",
    icon: Car01,
    description: "Pipeline of total-loss cases",
    roles: [Roles.Director, Roles.GeneralManager, Roles.ServiceManager, Roles.ServiceAdvisor, Roles.SalesManager, Roles.SalesRepresentative],
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings01,
    description: "Profile, team, fees",
    roles: null,
  },
]

function matchRoute(pathname: string, href: string, excludeChildren?: string) {
  if (pathname === href) return true
  if (pathname.startsWith(`${href}/`)) {
    if (excludeChildren && pathname.startsWith(excludeChildren)) return false
    return true
  }
  return false
}

function initialsOf(name: string) {
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
}

/* ── Bottom nav ──────────────────────────────────────────── */

export function MobileBottomNav() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const currentRole = useCurrentRole()
  const { activeTeamKind } = useOrganization()
  const [moreOpen, setMoreOpen] = useState(false)

  const isAdvisor = currentRole === Roles.ServiceAdvisor && !user?.admin
  const isQEActive = pathname === "/estimates/quick" || pathname.startsWith("/estimates/quick/")
  // Pass current page as `?return=` so the wizard's leave-confirm
  // sends users back here (ClickUp 869d7d7xz). If we're already
  // inside the wizard, skip — the link still works as a "restart"
  // and the dashboard fallback covers re-entry.
  const qeHref = isQEActive
    ? "/estimates/quick"
    : `/estimates/quick?return=${encodeURIComponent(pathname)}`

  const handleSignOut = async () => {
    setMoreOpen(false)
    await signOut()
    navigate("/login")
  }

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-40">
        <div className="relative">
          {/* Speed dial — anchored at exact horizontal centre of the
              viewport (`left-1/2 -translate-x-1/2`) so the dominant mobile
              action sits at the natural focal point. The 5px white ring +
              dramatic emerald glow + lift make it read as a "raised" action
              floating out of the bar, not a tab. */}
          <motion.div
            initial={{ scale: 0.6, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 280, damping: 22, delay: 0.05 }}
            className="absolute left-1/2 -translate-x-1/2 -top-7 z-10"
          >
            <Link
              to={qeHref}
              aria-label="Start a quick estimate"
              className={cx(
                "size-[68px] rounded-full grid place-items-center",
                "bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-700",
                "ring-[6px] ring-white",
                "shadow-[0_14px_30px_-4px_rgba(16,185,129,0.6),0_5px_10px_-2px_rgba(16,185,129,0.35)]",
                "hover:from-emerald-500 hover:to-emerald-800 hover:shadow-[0_18px_36px_-4px_rgba(16,185,129,0.65),0_5px_12px_-2px_rgba(16,185,129,0.4)]",
                "active:scale-95 transition-all duration-200",
                isQEActive && "ring-emerald-100",
              )}
            >
              <ZapFast className="size-[30px] text-white" strokeWidth={2.4} aria-hidden />
            </Link>
          </motion.div>

          {/* The bar — 3 equal columns. Column 2 is dead-centre of the
              viewport (33.3% – 66.7%, centre at 50%) so the absolutely
              positioned dial above it sits at true horizontal centre. All
              three slots use the same NavTabContent shape (with a null
              icon for the dial slot) so labels share an identical baseline. */}
          {/* The bar floats — rounded, ringed, deep-shadowed. A designed
              object, not a browser default strip. */}
          <div
            className="mx-3 mb-2 rounded-[26px] bg-white/95 backdrop-blur-xl ring-1 ring-zinc-900/[0.06] shadow-[0_18px_40px_-12px_rgba(24,24,27,0.28),0_2px_8px_-2px_rgba(24,24,27,0.08)]"
            style={{ marginBottom: "max(8px, env(safe-area-inset-bottom))" }}
          >
            {isAdvisor ? (
              /* Service Advisor — the phone-app bar the client's world
                 expects: five focused tabs, capture dead-center. More must
                 stay a slot: it's the only mobile path to Settings, the team
                 switcher and Sign out. Referrals lives inside the More sheet
                 (2 taps) — matches the bar shipped on main. */
              <div className="grid grid-cols-5 items-stretch h-[72px] px-1">
                <NavTab
                  href="/dashboard"
                  icon={HomeLine}
                  label="Today"
                  active={matchRoute(pathname, "/dashboard")}
                />
                <NavTab
                  href="/leaders"
                  icon={Trophy01}
                  label="Leaders"
                  active={matchRoute(pathname, "/leaders")}
                />
                <Link
                  to={qeHref}
                  className="flex flex-col items-center justify-center gap-1 h-full"
                  aria-label="Start a quick estimate"
                >
                  <NavTabContent icon={null} label="Start" active={isQEActive} variant="dial" />
                </Link>
                <NavTab
                  href="/wallet"
                  icon={Wallet01}
                  label="Rewards"
                  active={matchRoute(pathname, "/wallet")}
                />
                <button
                  type="button"
                  onClick={() => setMoreOpen(true)}
                  className="flex flex-col items-center justify-center gap-1 h-full"
                  aria-label="More"
                >
                  <NavTabContent icon={Menu01} label="More" active={moreOpen} />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 items-stretch h-[72px] px-2">
                <NavTab
                  href={PRIMARY_TABS[0].href}
                  icon={PRIMARY_TABS[0].icon}
                  label={PRIMARY_TABS[0].name}
                  active={matchRoute(pathname, PRIMARY_TABS[0].href)}
                />

                <Link
                  to={qeHref}
                  className="flex flex-col items-center justify-center gap-1 h-full"
                  aria-label="Start a quick estimate"
                >
                  <NavTabContent icon={null} label="Quick" active={isQEActive} variant="dial" />
                </Link>

                <button
                  type="button"
                  onClick={() => setMoreOpen(true)}
                  className="flex flex-col items-center justify-center gap-1 h-full"
                  aria-label="More"
                >
                  <NavTabContent icon={Menu01} label="More" active={moreOpen} />
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <MoreSheet
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        items={MORE_ITEMS.filter((i) => !i.teamKinds || (activeTeamKind != null && i.teamKinds.includes(activeTeamKind)))
          .filter((i) => !i.roles || user?.admin || (currentRole !== null && i.roles.includes(currentRole)))}
        userName={user?.name ?? ""}
        userEmail={user?.email ?? ""}
        roleLabel={user?.admin ? "Admin" : currentRole ?? ""}
        onSignOut={handleSignOut}
      />
    </>
  )
}

/* ── Tab visual ──────────────────────────────────────────── */

function NavTab({
  href,
  icon,
  label,
  active,
}: {
  href: string
  icon: FC<{ className?: string; strokeWidth?: number }>
  label: string
  active: boolean
}) {
  return (
    <Link to={href} className="flex flex-col items-center justify-center gap-1 h-full">
      <NavTabContent icon={icon} label={label} active={active} />
    </Link>
  )
}

// Single shared cell shape — `icon` may be null for the dial slot, in which
// case we still render a 22×22 transparent block inside the identical
// `relative flex flex-col items-center` wrapper. That keeps every cell's
// DOM structure bit-for-bit identical across active / inactive / dial
// states, so all four labels share the same baseline.
function NavTabContent({
  icon: Icon,
  label,
  active,
  variant = "tab",
}: {
  icon: FC<{ className?: string; strokeWidth?: number }> | null
  label: string
  active: boolean
  // The dial cell is "quiet" — no active emerald bar above the icon (the
  // floating dial itself signals active), and the label tints emerald
  // instead of zinc-900 when active.
  variant?: "tab" | "dial"
}) {
  return (
    <>
      <div className="relative flex flex-col items-center">
        {/* Active = a soft emerald plate behind the icon — a designed state,
            not a dot. The plate is always in the DOM so baselines never shift. */}
        <span
          className={cx(
            "grid place-items-center size-9 -my-[7px] rounded-2xl transition-all duration-200",
            variant === "tab" && active ? "bg-emerald-500/12" : "bg-transparent",
          )}
        >
          {Icon ? (
            <Icon
              className={cx(
                "size-[22px] transition-colors duration-150",
                active ? "text-emerald-700" : "text-zinc-400",
              )}
              strokeWidth={active ? 2.1 : 1.6}
              aria-hidden
            />
          ) : (
            <span className="size-[22px] block" aria-hidden />
          )}
        </span>
      </div>
      <span
        className={cx(
          "text-[10px] font-bold leading-none transition-colors duration-150 tracking-[0.01em]",
          variant === "dial"
            ? active
              ? "text-emerald-700"
              : "text-zinc-400"
            : active
              ? "text-emerald-800"
              : "text-zinc-400",
        )}
      >
        {label}
      </span>
    </>
  )
}

/* ── More sheet ──────────────────────────────────────────── */

function MoreSheet({
  open,
  onClose,
  items,
  userName,
  userEmail,
  roleLabel,
  onSignOut,
}: {
  open: boolean
  onClose: () => void
  items: MoreItem[]
  userName: string
  userEmail: string
  roleLabel: string
  onSignOut: () => void
}) {
  return (
    <Sheet isOpen={open} onClose={onClose} title="More">
      <div className="space-y-5">
        {/* Active team — chip + switcher entry. Tapping opens the full team
            picker; for single-team users this stays compact. */}
        <ActiveTeamCard onAfterSelect={onClose} />

        {/* User card */}
        {userName && (
          <div className="rounded-2xl border border-zinc-200/60 bg-zinc-50/60 px-4 py-3 flex items-center gap-3">
            <div className="size-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white text-[13px] font-bold shadow-md shadow-emerald-600/25">
              {initialsOf(userName) || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-semibold text-zinc-900 truncate">{userName}</p>
              <p className="text-[11.5px] text-zinc-500 truncate">{userEmail}</p>
            </div>
            {roleLabel && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-white text-zinc-600 ring-1 ring-zinc-200">
                {roleLabel}
              </span>
            )}
          </div>
        )}

        {/* Menu items */}
        <div className="space-y-1">
          {items.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-zinc-50 active:bg-zinc-100 transition-colors duration-100"
              >
                <div className="size-10 rounded-xl bg-zinc-100 grid place-items-center shrink-0">
                  <Icon className="size-[18px] text-zinc-600" strokeWidth={1.8} aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-zinc-900">{item.name}</p>
                  <p className="text-[11.5px] text-zinc-500 truncate">{item.description}</p>
                </div>
                <ChevronRight className="size-4 text-zinc-300 shrink-0" aria-hidden />
              </Link>
            )
          })}
        </div>

        {/* Sign out */}
        <div className="pt-2 border-t border-zinc-100">
          <button
            type="button"
            onClick={onSignOut}
            className="flex items-center gap-3 w-full p-2.5 rounded-2xl text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors duration-100"
          >
            <div className="size-10 rounded-xl bg-red-50 grid place-items-center shrink-0">
              <LogOut01 className="size-[18px] text-red-500" strokeWidth={1.8} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-[14px] font-semibold">Sign out</p>
              <p className="text-[11.5px] text-red-500/70">End your session</p>
            </div>
          </button>
        </div>
      </div>
    </Sheet>
  )
}

/* ── Active team card (with inline switcher) ────────────────── */

function ActiveTeamCard({ onAfterSelect }: { onAfterSelect: () => void }) {
  const {
    activeGroup,
    availableGroups,
    groupDealerships,
    activeDealership,
    activeBodyShop,
    availableDealerships,
    availableBodyShops,
    activeTeamKind,
    isGroupView,
    setActiveGroup,
    setActiveTeam,
    setGroupView,
  } = useOrganization()

  const dealershipChoices = activeGroup ? groupDealerships : availableDealerships
  const showSwitcher =
    availableGroups.length > 0 ||
    availableBodyShops.length > 0 ||
    availableDealerships.length > 1
  const [expanded, setExpanded] = useState(false)

  // What we display as "active" on the chip.
  const activeKind: "group-view" | "group" | "dealership" | "bodyshop" | "none" = (() => {
    if (isGroupView && activeGroup) return "group-view"
    if (activeTeamKind === "dealership" && activeDealership) return "dealership"
    if (activeTeamKind === "bodyshop" && activeBodyShop) return "bodyshop"
    if (activeGroup) return "group"
    return "none"
  })()

  const tokens =
    activeKind === "dealership"
      ? ORG_VISUAL_TOKENS.dealership
      : activeKind === "bodyshop"
        ? ORG_VISUAL_TOKENS.bodyshop
        : ORG_VISUAL_TOKENS.group

  const Icon =
    activeKind === "dealership"
      ? Car01
      : activeKind === "bodyshop"
        ? Tool01
        : LayersThree01

  const activeName =
    activeKind === "group-view"
      ? activeGroup!.name
      : activeKind === "group"
        ? activeGroup!.name
        : activeKind === "dealership"
          ? activeDealership!.name
          : activeKind === "bodyshop"
            ? activeBodyShop!.name
            : "No team"

  const activeSub =
    activeKind === "group-view"
      ? `All ${groupDealerships.length} locations`
      : activeKind === "dealership" && activeDealership
        ? [activeDealership.city, activeDealership.state].filter(Boolean).join(", ") || "Dealership"
        : activeKind === "bodyshop" && activeBodyShop
          ? [activeBodyShop.city, activeBodyShop.state].filter(Boolean).join(", ") || "Body shop"
          : tokens.label

  if (!showSwitcher) {
    // Single-team user — no need for the switcher, just show identity.
    return (
      <div className="rounded-2xl border border-zinc-200/60 bg-white px-4 py-3 flex items-center gap-3">
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            tokens.gradient,
            tokens.shadow,
          )}
        >
          <Icon className="size-[18px] text-white" strokeWidth={2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold text-zinc-900 truncate">{activeName}</p>
          <p className="text-[11.5px] text-zinc-500 truncate">{activeSub}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-zinc-200/60 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 p-3 hover:bg-zinc-50/70 transition-colors duration-100"
      >
        <div
          className={cx(
            "size-10 rounded-xl grid place-items-center shrink-0 shadow-md",
            tokens.gradient,
            tokens.shadow,
          )}
        >
          <Icon className="size-[18px] text-white" strokeWidth={2} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-[14px] font-semibold text-zinc-900 truncate">{activeName}</p>
          <p className="text-[11.5px] text-zinc-500 truncate">{activeSub}</p>
        </div>
        <span className="text-[11px] font-semibold text-zinc-400 shrink-0">
          {expanded ? "Close" : "Switch"}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-zinc-100 p-3 space-y-3">
          {/* Groups */}
          {availableGroups.length > 0 && (
            <SwitcherSection title={availableGroups.length > 1 ? "Groups" : "Group"}>
              {availableGroups.map((g) => {
                const active = activeKind === "group" && activeGroup?.id === g.id
                return (
                  <SwitcherRow
                    key={g.id}
                    icon={LayersThree01}
                    tokens={ORG_VISUAL_TOKENS.group}
                    title={g.name}
                    sub={`Group · ${groupDealerships.length} ${
                      groupDealerships.length === 1 ? "location" : "locations"
                    }`}
                    active={active}
                    onClick={() => {
                      setActiveGroup(g.id)
                      setGroupView(false)
                      setExpanded(false)
                      onAfterSelect()
                    }}
                  />
                )
              })}
              {activeGroup && groupDealerships.length > 0 && (
                <SwitcherRow
                  icon={LayersThree01}
                  tokens={ORG_VISUAL_TOKENS.group}
                  title="All locations"
                  sub={`Roll-up across ${activeGroup.name}`}
                  active={activeKind === "group-view"}
                  onClick={() => {
                    setGroupView(true)
                    setExpanded(false)
                    onAfterSelect()
                  }}
                />
              )}
            </SwitcherSection>
          )}

          {dealershipChoices.length > 0 && (
            <SwitcherSection title="Dealerships">
              {dealershipChoices.map((d) => {
                const active = activeKind === "dealership" && activeDealership?.id === d.id
                return (
                  <SwitcherRow
                    key={d.id}
                    icon={Car01}
                    tokens={ORG_VISUAL_TOKENS.dealership}
                    title={d.name}
                    sub={[d.city, d.state].filter(Boolean).join(", ") || "Dealership"}
                    active={active}
                    onClick={() => {
                      setActiveTeam("dealership", d.id)
                      setExpanded(false)
                      onAfterSelect()
                    }}
                  />
                )
              })}
            </SwitcherSection>
          )}

          {availableBodyShops.length > 0 && (
            <SwitcherSection title="Body shops">
              {availableBodyShops.map((bs) => {
                const active = activeKind === "bodyshop" && activeBodyShop?.id === bs.id
                return (
                  <SwitcherRow
                    key={bs.id}
                    icon={Tool01}
                    tokens={ORG_VISUAL_TOKENS.bodyshop}
                    title={bs.name}
                    sub={[bs.city, bs.state].filter(Boolean).join(", ") || "Body shop"}
                    active={active}
                    onClick={() => {
                      setActiveTeam("bodyshop", bs.id)
                      setExpanded(false)
                      onAfterSelect()
                    }}
                  />
                )
              })}
            </SwitcherSection>
          )}
        </div>
      )}
    </div>
  )
}

function SwitcherSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-400 px-1 mb-1.5">
        {title}
      </p>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function SwitcherRow({
  icon: Icon,
  tokens,
  title,
  sub,
  active,
  onClick,
}: {
  icon: FC<{ className?: string; strokeWidth?: number }>
  tokens: (typeof ORG_VISUAL_TOKENS)[keyof typeof ORG_VISUAL_TOKENS]
  title: string
  sub: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "w-full flex items-center gap-3 p-2 rounded-xl text-left transition-colors duration-100",
        active ? "bg-emerald-50/70" : "hover:bg-zinc-50",
      )}
    >
      <div
        className={cx(
          "size-9 rounded-xl grid place-items-center shrink-0 shadow-md",
          tokens.gradient,
          tokens.shadow,
        )}
      >
        <Icon className="size-[18px] text-white" strokeWidth={2} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cx(
            "text-[13px] font-semibold truncate",
            active ? "text-emerald-900" : "text-zinc-900",
          )}
        >
          {title}
        </p>
        <p
          className={cx(
            "text-[11px] truncate",
            active ? "text-emerald-700/70" : "text-zinc-400",
          )}
        >
          {sub}
        </p>
      </div>
      {active ? (
        <span className="size-5 rounded-full bg-emerald-500 grid place-items-center shrink-0 shadow-sm shadow-emerald-600/30">
          <ArrowRight className="size-3 text-white" strokeWidth={3} aria-hidden />
        </span>
      ) : (
        <ChevronRight className="size-4 text-zinc-300 shrink-0" aria-hidden />
      )}
    </button>
  )
}
